import { LinkBreak } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { TesterEntryView, TesterPageView } from "@plannello/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { testerApi, testerPageQuery } from "@/components/feedback/api";
import { Composer, type ComposerHandle } from "@/components/feedback/Composer";
import { isNewForTester, TesterHistory } from "@/components/feedback/TesterHistory";
import { PublicShell } from "@/components/shell/PublicShell";
import { Callout, InlineError, Skeleton } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { applyLocale } from "@/lib/locale";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/f/$token")({ component: TesterPage });

/** The link opens in the language its creator chose, once per browser session; the switch overrides it. */
function useLinkLocale(token: string, locale: "en" | "de" | undefined) {
  useEffect(() => {
    if (!locale) return;
    const key = `pt-feedback-locale:${token.slice(0, 12)}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // ignore
    }
    applyLocale(locale);
  }, [token, locale]);
}

/** Search engines must not index a page whose URL is a secret. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const title = document.title;
    document.title = m.fb_doc_title();
    return () => {
      meta.remove();
      document.title = title;
    };
  }, []);
}

function TesterPage() {
  const { token } = Route.useParams();
  const q = useQuery(testerPageQuery(token));
  useNoIndex();
  useLinkLocale(token, q.data?.locale);

  if (q.isPending) {
    return (
      <PublicShell>
        <div role="status" aria-busy="true" className="flex flex-col gap-6">
          <span className="sr-only">{m.common_loading()}</span>
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-full max-w-md" />
          <Skeleton className="h-72 w-full rounded-panel" />
          <Skeleton className="h-5 w-40" />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-panel" />
          ))}
        </div>
      </PublicShell>
    );
  }

  if (q.isError) {
    const dead = q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 410);
    return (
      <PublicShell>
        {dead ? (
          <div className="mx-auto mt-[8vh] flex max-w-md flex-col items-center text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-hover text-fg-muted">
              <LinkBreak size={24} aria-hidden="true" />
            </span>
            <h1 className="mt-5 text-2xl font-semibold tracking-tight text-pretty">{m.fb_dead_title()}</h1>
            <p className="mt-2 text-sm text-pretty text-fg-muted">{m.fb_dead_body()}</p>
          </div>
        ) : (
          <InlineError
            message={
              q.error instanceof ApiError && q.error.status === 429 ? m.error_rate_limited() : m.fb_err_offline()
            }
            onRetry={() => void q.refetch()}
            isRetrying={q.isFetching}
          />
        )}
      </PublicShell>
    );
  }

  return <TesterContent token={token} page={q.data} />;
}

function TesterContent({ token, page }: { token: string; page: TesterPageView }) {
  const qc = useQueryClient();
  const [followUp, setFollowUp] = useState<TesterEntryView | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const [revealNews, setRevealNews] = useState(0);
  const composer = useRef<ComposerHandle>(null);
  const composerBox = useRef<HTMLDivElement>(null);
  const sentRef = useRef<HTMLDivElement>(null);

  const setEntries = (entries: TesterEntryView[]) =>
    qc.setQueryData(testerPageQuery(token).queryKey, (old) => (old ? { ...old, entries } : old));

  const news = useMemo(() => {
    const fresh = page.entries.filter((e) => isNewForTester(e, page.lastSeenAt));
    const count = (s: TesterEntryView["status"]) => fresh.filter((e) => e.status === s).length;
    return {
      total: fresh.length,
      done: count("done"),
      progress: count("in_progress"),
      planned: count("planned"),
      not: count("not_planned"),
    };
  }, [page.entries, page.lastSeenAt]);

  useEffect(() => {
    if (sent !== null) sentRef.current?.focus();
  }, [sent]);

  const startFollowUp = (e: TesterEntryView) => {
    setFollowUp(e);
    setSent(null);
    composerBox.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    requestAnimationFrame(() => composer.current?.focus());
  };

  const withdraw = async (e: TesterEntryView) => {
    try {
      const res = await testerApi.withdraw(token, e.id);
      setEntries(res.entries);
      toast.undo(
        m.fb_withdrawn(),
        () =>
          void testerApi
            .restore(token, e.id)
            .then((r) => setEntries(r.entries))
            .catch(() => toast.show({ title: m.fb_err_generic(), tone: "danger" })),
        m.common_undo(),
      );
    } catch (err) {
      toast.show({
        title:
          err instanceof ApiError && err.reason === "already_resolved" ? m.fb_withdraw_too_late() : m.fb_err_generic(),
        tone: "danger",
      });
      if (err instanceof ApiError && err.reason === "already_resolved")
        void qc.invalidateQueries({ queryKey: ["tester", token] });
    }
  };

  const summary = [
    news.done ? (news.done === 1 ? m.fb_news_done_one() : m.fb_news_done({ count: news.done })) : null,
    news.progress ? m.fb_news_progress({ count: news.progress }) : null,
    news.planned ? m.fb_news_planned({ count: news.planned }) : null,
    news.not ? m.fb_news_not_planned({ count: news.not }) : null,
  ].filter(Boolean);

  return (
    <PublicShell eyebrow={page.products.length === 1 ? page.products[0]!.name : m.fb_eyebrow()}>
      <header className="max-w-2xl">
        <h1 className="text-2xl font-semibold tracking-tight text-pretty sm:text-3xl">
          {m.fb_greeting({ name: page.label })}
        </h1>
        <p className="mt-2 text-base text-pretty text-fg-muted">
          {page.ownerName ? m.fb_intro({ owner: page.ownerName }) : m.fb_intro_anonymous()}
        </p>
      </header>

      {news.total > 0 ? (
        <Callout tone="success" title={m.fb_news_title()} className="mt-6 max-w-2xl">
          {summary.join(", ")}.{" "}
          <AriaButton
            onPress={() => setRevealNews((n) => n + 1)}
            className="cursor-pointer rounded-mark font-medium underline underline-offset-2 outline-hidden data-focus-visible:outline-2 data-focus-visible:outline-focus data-focus-visible:outline-solid data-hovered:no-underline"
          >
            {m.fb_news_show()}
          </AriaButton>
        </Callout>
      ) : null}

      {sent !== null ? (
        <div ref={sentRef} tabIndex={-1} className="mt-6 max-w-2xl outline-none">
          <Callout
            tone="success"
            role="status"
            title={sent === 1 ? m.fb_sent_title_one() : m.fb_sent_title({ count: sent })}
          >
            {m.fb_sent_body()}
          </Callout>
        </div>
      ) : null}

      <div ref={composerBox} className="mt-8 scroll-mt-6">
        <Composer
          ref={composer}
          token={token}
          page={page}
          followUp={followUp}
          onClearFollowUp={() => setFollowUp(null)}
          onSent={(entries, count) => {
            setEntries(entries);
            setSent(count);
          }}
        />
      </div>

      <TesterHistory
        token={token}
        page={page}
        revealNews={revealNews}
        onFollowUp={startFollowUp}
        onWithdraw={(e) => void withdraw(e)}
      />
    </PublicShell>
  );
}
